<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/Global_Projections';
    let nextLink= '/Documentation/Defining_an_Editor/Fragments';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="adding-buttons-1"
  bind:intersecting={visibleById["adding-buttons-1"]}
  onIntersectChange={(visible) => setVisible("adding-buttons-1", visible)}
>Adding Buttons</SectionComponent>
<p>It is possible to add an extra button to any projection. To do so, add
the following line in one of the projections. There must be no space between
the opening square bracket <code>[</code> and <code>button</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-fragments.edit#L18-L18

[button text="Push me!" boxRole="MyButton-role"]</code></pre>
<p>The <strong>text</strong> is the text that will be shown on the button. The <strong>boxRole</strong> defines how the button is linked to an action.
For this you need to create a <a
  href="/Documentation/Customizations/Editor_Customization#writing-custom-actions-4"
>custom action</a>. </p>
<p>Note that in the custom action you need to specify the
exact same <strong>boxRole</strong> as the one that has been supplied with the button in the .edit file.
When implementing this custom action, you can use <code>box.node</code> to get the node in the AST associated with the button.
For more information see <a
  href="/Documentation/Customizations/Editor_Customization#writing-custom-actions-4"
>Writing_Actions</a>.</p>
<p>Work is ongoing to expose the built-in actions in the editor core.</p>
<SectionComponent tag="h2" 
  id="icon-buttons-2"
  bind:intersecting={visibleById["icon-buttons-2"]}
  onIntersectChange={(visible) => setVisible("icon-buttons-2", visible)}
>Icon Buttons</SectionComponent>
<p>You can add an icon before or after the text using CSS or SCSS; the text itself is optional.</p>
<p>As an example, buttons are added to a table definition.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-tables-with-button.edit#L6-L11

InsurancePart&#123;
table [
    Name    | risk               | pay out          | is approved   | action
    $&#123;name&#125; | $&#123;statisticalRisk&#125; | $&#123;maximumPayOut&#125; | $&#123;isApproved&#125; | [button boxRole="MyTableButton-role"]
]
&#125;</code></pre>
<p>To style these buttons the following SCSS code is added.</p>
<pre class="language-scss"><code class="language-scss">// Styling/button.css#L9-L31

.MyButton-role::after &#123;
  font-family: 'Material Icons', emoji;
  content: "\e5d2"; // this number is called the icon's code point in Material
  color: green;
  font-size: 25px;
  rotate: 30deg;
&#125;

.MyTableButton-role::before &#123;
  font-family: "Font Awesome 6 Free", emoji;
  font-weight: 900;
  //content: 'plus';
  content: '\\2b'; // this number is called the icon's unicode in Font Awesome
  color: dodgerblue;
  font-size: 16px;
  rotate: 30deg;
&#125;

.MyTableButton-role &#123;
  min-width: 0.5em !important;
  min-height: 0.5em !important;
  border-radius: 50% !important;
&#125;</code></pre>
<p>This is displayed as follows.</p>
<Figure
imageName={'documentation/Documentation-Buttons-screenshot1.png'}
caption={'Showing a button in each table row'}
figureNumber={1}
/>


<PrevNextSection {prevLink} {nextLink} />